"use client";

import type { LeadFlowReviewStars } from "@homeinstead/schemas";

import { formatNumber } from "@homeinstead/ui";
import {
  RatingStars,
  roundToNearestTenth,
} from "@homeinstead/ui/components/Rating";
import { isDisplayableRating } from "~www/lib/office/get-aggregate-rating";
import { useLeadFlowRuntime } from "../LeadFlowRuntimeContext";

const DEFAULT_REVIEW_TEXT =
  "{{reviewStarCount}} star rating on {{reviewPlatform}} ({{roundedReviewCount}} reviews)";

const DEFAULT_ARIA_LABEL = "Rating: {{reviewStarCount}} out of 5";

type ReviewStarsItemProps = {
  item: Partial<Omit<LeadFlowReviewStars, "_type">>;
};

type ReviewTextValues = {
  reviewPlatform: string;
  reviewStarCount: number;
  roundedReviewCount: string;
};

function fillReviewTemplate(template: string, values: ReviewTextValues) {
  return template
    .replaceAll("{{reviewStarCount}}", String(values.reviewStarCount))
    .replaceAll("{{reviewPlatform}}", values.reviewPlatform)
    .replaceAll("{{roundedReviewCount}}", values.roundedReviewCount);
}

function ReviewStarsItem({ item }: ReviewStarsItemProps) {
  const { officeRating, themeContrast } = useLeadFlowRuntime();

  let rating: number;
  let reviewCount: number;

  if (item.source === "manual") {
    if (!item.manualRating || item.manualReviewCount == null) {
      return null;
    }
    rating = roundToNearestTenth(item.manualRating);
    reviewCount = item.manualReviewCount;
  } else {
    if (!isDisplayableRating(officeRating)) {
      return null;
    }
    rating = roundToNearestTenth(officeRating.rating);
    reviewCount = officeRating.ratingCount;
  }

  const values: ReviewTextValues = {
    reviewPlatform: item.reviewPlatform || "Google",
    reviewStarCount: rating,
    roundedReviewCount: formatNumber(reviewCount),
  };

  return (
    <div className="flex w-fit flex-row items-center gap-3 font-bold">
      <RatingStars
        ariaLabel={fillReviewTemplate(
          item.ariaLabel || DEFAULT_ARIA_LABEL,
          values,
        )}
        color={
          item.starVariant === "gold"
            ? "gold"
            : themeContrast === "light"
              ? "white"
              : "default"
        }
        rating={rating}
      />
      <span className="text-sm">
        {fillReviewTemplate(item.reviewText || DEFAULT_REVIEW_TEXT, values)}
      </span>
    </div>
  );
}

export { ReviewStarsItem };
